/**
 * Push delivery for the daily reminder, for signed-in learners.
 *
 * The device-only reminder (services/reminders.js) cannot reach a closed app on
 * most browsers. When a learner is signed in and has the reminder on, this
 * registers the device's push address with the database; a small sender on our
 * server (deploy/push/) then delivers the reminder through the browser's push
 * service, only on days the learner has not practised yet.
 *
 * `syncPushRegistration()` makes the device match what is wanted: registered with
 * the current time and time zone, or unregistered. It is safe to call at any time
 * and does nothing where push is unavailable (no service worker in development,
 * unsupported browsers, accounts not configured).
 */

import { getSupabase, isSupabaseConfigured } from '../lib/supabaseClient.js';
import { getSettings } from '../app/state.js';
import { getCurrentUser } from './authService.js';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

/** The server's public key in the form the browser's subscribe() needs. */
export function urlBase64ToUint8Array(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4);
  const binary = atob(base64);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** Arguments for the save_push_subscription database function. */
export function registrationArgs(subscription, time, zone) {
  return {
    p_endpoint: subscription.endpoint,
    p_p256dh: subscription.keys?.p256dh,
    p_auth: subscription.keys?.auth,
    p_time: time,
    p_zone: zone || 'Asia/Kolkata'
  };
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    Boolean(VAPID_PUBLIC_KEY) &&
    isSupabaseConfigured()
  );
}

/** Whether this device should currently be registered for push reminders. */
function wanted() {
  return Boolean(getSettings().reminderEnabled) && Notification.permission === 'granted' && Boolean(getCurrentUser());
}

let queue = Promise.resolve();

/** Register or unregister this device so it matches the settings. Calls are run one after another. */
export function syncPushRegistration() {
  queue = queue.then(run).catch((error) => console.warn('[push] could not update the push registration', error));
  return queue;
}

async function run() {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const existing = await registration.pushManager.getSubscription();

  if (wanted()) {
    const subscription =
      existing ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      }));
    const client = await getSupabase();
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { error } = await client.rpc('save_push_subscription', registrationArgs(subscription.toJSON(), getSettings().reminderTime, zone));
    if (error) throw error;
    return;
  }

  if (existing) {
    // Still signed in: remove the address from the server too. Signed out: we cannot,
    // but unsubscribing makes the address invalid and the sender then drops it.
    if (getCurrentUser()) {
      const client = await getSupabase();
      await client.rpc('delete_push_subscription', { p_endpoint: existing.endpoint });
    }
    await existing.unsubscribe();
  }
}
